// components/ui/SegmentSwitch.tsx
'use client';

export interface SegmentSwitchOption<K extends string> {
  key: K;
  label: string;
  /** Optional tour anchor, e.g. data-factory-tour. */
  tour?: { attribute: string; value: string };
}

interface SegmentSwitchProps<K extends string> {
  groupLabel: string;
  options: readonly [SegmentSwitchOption<K>, SegmentSwitchOption<K>];
  value: K;
  onChange: (next: K) => void;
  className?: string;
}

/**
 * ChatGPT's two-way switch (its Chat / Work control), the one every module home uses to flip view:
 * Source (Shortlist / Assets), Slides (Dashboard / Training), List (Dashboard / Factory). Measured
 * off ChatGPT's screenshot (owner, 2026-09-28): a 34px track in #131313 at least 224px wide, a
 * #1B1B1B thumb a little wider than half and 3px taller than the track that slides, 15px words.
 * Both halves take the longer label's width, so a French label never squeezes. Pointer-down is
 * swallowed so a click never steals focus from an input mid-edit.
 */
export function SegmentSwitch<K extends string>({ groupLabel, options, value, onChange, className }: SegmentSwitchProps<K>) {
  const second = value === options[1].key;
  return <div role="group" aria-label={groupLabel} className={`relative inline-grid h-[34px] min-w-[224px] shrink-0 grid-cols-2 rounded-full bg-[#131313] ${className ?? ''}`}>
    <span aria-hidden="true" className="pointer-events-none absolute -bottom-0.5 -top-px w-[53.5%] rounded-full bg-[#1B1B1B] motion-safe:transition-[left] motion-safe:duration-200 motion-safe:ease-out" style={{ left: second ? '46.5%' : 0 }} />
    {options.map(({ key, label, tour }) => {
      const active = value === key;
      return <button key={key} type="button" aria-pressed={active}
        {...(tour ? { [tour.attribute]: tour.value } : {})}
        onPointerDown={event => event.preventDefault()}
        onClick={() => { if (!active) onChange(key); }}
        className={`relative min-w-0 whitespace-nowrap rounded-full px-4 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8AAAEE] ${active ? 'text-white' : 'text-[#CDCDCD] hover:text-white'}`}
        style={{ fontSize: 15 }}>{label}</button>;
    })}
  </div>;
}
